import axios from 'axios';

import Storage from 'storage';
import store from 'store';

import {
  clearSession,
  getApiEndpoint,
  missingEndpointError,
} from 'utils/session';

const GET = (url: string, auth?: CustomAuth) => {
  const token = auth?.token || Storage.get('user')?.token;
  const endpoint = auth?.endpoint || getApiEndpoint();

  if (!endpoint) return Promise.reject(missingEndpointError);

  return axios
    .get(endpoint + url, {
      headers: {
        ...(auth ? { Token: token } : { User: token }),
      },
    })
    .then(function (response) {
      return response.data;
    })
    .catch(function (error) {
      return new Promise(function (_, reject) {
        if (!error.response) {
          reject({ error: 'No response', message: error });
          return;
        }

        // Handle invalid token reponse
        if ([401].includes(error.response.status)) {
          clearSession();
          window.location.reload();
        }

        // Handle invalid request response. Mainly for invalid form data
        if ([403, 404].includes(error.response.status)) {
          reject(error.response.data);
        }

        // Handle all other unforeseen unhandled errors
        reject({ error: 'Unhandled error', message: error });
      });
    });
};

const DELETE = (url: string, data?: any) => {
  const userToken = Storage.get('user')?.token;
  const endpoint = getApiEndpoint();

  if (!endpoint) return Promise.reject(missingEndpointError);

  return axios
    .delete(endpoint + url, {
      headers: { User: userToken },
      data: data,
    })
    .then(function (response) {
      return response.data;
    })
    .catch(function (error) {
      return new Promise(function (_, reject) {
        // Handle no response errors. Mainly the generic "Network error"
        if (!error.response) {
          reject({ error: 'No response', message: error });
        }

        // Handle invalid token reponse
        if ([401].includes(error.response.status)) {
          clearSession();
          window.location.reload();
        }

        // Preserve API validation and lock messages for all client errors.
        if (
          store.getState().featureFlags.flags['enable-bara-referral-fixes']
            ?.is_enabled
            ? error.response.status >= 400 && error.response.status < 500
            : [400, 403].includes(error.response.status)
        ) {
          reject(error.response.data);
          return;
        }

        // Handle all other unforeseen unhandled errors
        reject({ error: 'Unhandled error', message: error });
      });
    });
};

const POST = (url: string, data: any) => {
  const userToken = Storage.get('user')?.token;
  const endpoint = getApiEndpoint();

  if (!endpoint) return Promise.reject(missingEndpointError);

  return axios
    .post(endpoint + url, data, {
      headers: { User: userToken },
      withCredentials: true,
    })
    .then(function (response) {
      return response.data;
    })
    .catch(function (error) {
      return new Promise(function (_, reject) {
        if (!error.response) {
          reject({ error: 'No response', message: error });
          return;
        }

        // Handle invalid token reponse
        if ([401].includes(error.response.status)) {
          clearSession();
          window.location.reload();
        }

        // Preserve API validation and lock messages for all client errors.
        if (
          store.getState().featureFlags.flags['enable-bara-referral-fixes']
            ?.is_enabled
            ? error.response.status >= 400 && error.response.status < 500
            : [400, 403].includes(error.response.status)
        ) {
          reject(error.response.data);
          return;
        }

        // Handle all other unforeseen unhandled errors
        reject({ error: 'Unhandled error', message: error });
      });
    });
};

const PATCH = (url: string, data: any) => {
  const userToken = Storage.get('user')?.token;
  const endpoint = getApiEndpoint();

  if (!endpoint) return Promise.reject(missingEndpointError);

  return axios
    .patch(endpoint + url, data, {
      headers: { User: userToken },
    })
    .then(function (response) {
      return response.data;
    })
    .catch(function (error) {
      return new Promise(function (_, reject) {
        if (!error.response) {
          reject({ error: 'No response', message: error });
        }

        // Handle invalid token reponse
        if ([401].includes(error.response.status)) {
          clearSession();
          window.location.reload();
        }

        // Handle invalid request response. Mainly for invalid form data
        if ([400, 403].includes(error.response.status)) {
          reject(error.response.data);
        }

        // Handle all other unforeseen unhandled errors
        reject({ error: 'Unhandled error', message: error });
      });
    });
};

export { GET, POST, PATCH, DELETE };
